'use client';

import { cn } from '@/lib/utils';
import { FC } from 'react';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from 'recharts';

export type RegionalReservationItem = {
  region: string;
  score: number;
  maxScore?: number;
};

export type ReservationsRadarChartProps = {
  data?: RegionalReservationItem[];
  className?: string;
  height?: number | string;
};

const RADAR_PLACEHOLDER_AXIS_COUNT = 5;

const CustomRadarDot = (props: { cx?: number; cy?: number }) => {
  const { cx, cy } = props;
  if (cx === undefined || cy === undefined) return null;
  return <circle cx={cx} cy={cy} r={4.5} stroke="var(--chart-radar)" strokeWidth={2.5} fill="var(--chart-radar-dot)" />;
};

export const ReservationsRadarChart: FC<ReservationsRadarChartProps> = ({ data = [], className, height = 320 }) => {
  const chartData = (
    data.length > 0
      ? data
      : Array.from({ length: RADAR_PLACEHOLDER_AXIS_COUNT }, () => ({ region: '', score: 0, maxScore: 100 }))
  ).map((d) => ({
    ...d,
    maxScore: d.maxScore ?? 100,
  }));
  const hasPlotData = chartData.some(({ score }) => score > 0);

  return (
    <div className={cn('w-full h-full min-h-[300px] flex items-center justify-center', className)}>
      <ResponsiveContainer width="100%" height={height} minWidth={0}>
        <RadarChart cx="50%" cy="50%" outerRadius="68%" data={chartData} startAngle={90} endAngle={-270}>
          <PolarGrid gridType="polygon" stroke="var(--chart-radar-grid)" strokeWidth={1.5} />
          <PolarAngleAxis dataKey="region" tick={{ fill: 'var(--chart-radar-label)', fontSize: 11, fontWeight: 600 }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />

          {/* Translucent sky-blue concentric fill */}
          <Radar
            name="background"
            dataKey="maxScore"
            stroke="none"
            fill="var(--chart-radar-area)"
            fillOpacity={0.6}
            isAnimationActive={false}
          />

          {/* Medium blue data polygon with white-centered node markers */}
          {hasPlotData && (
            <Radar
              name="Reservations"
              dataKey="score"
              stroke="var(--chart-radar)"
              strokeWidth={2.5}
              fill="var(--chart-radar)"
              fillOpacity={0.2}
              dot={<CustomRadarDot />}
              isAnimationActive={false}
            />
          )}
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ReservationsRadarChart;
